<template>
	<scroll-frame v-if="visible" ref="itemsContainer" :class="[frameClasses, errorClass]">
		<template v-if="!!items.header || errors.length > 0" #head>
			<api-error v-for="e in errors" :level="e.level" :message="e.message"></api-error>
			<component :is="componentOfPart(items.header)" :part="partRef(items.header)" :data="data" :readonly="ro" :viewmode="vm"></component>
		</template>
		<template #default>
			<table class="agenda" :class="[clazz, { 'inside-widgets': insideWidgets }]" :style="tableStyle" ref="itemsTable">
				<colgroup v-if="structure.colStyles"><col v-for="cs in structure.colStyles" :style="cs"></colgroup>
				<thead>
					<tr v-for="r in structure.header">
						<component v-for="c in r.cells" :is="c.component" v-bind="cellBind(c)"></component>
					</tr>
				</thead>
				<tbody @dragover="onDragOver" @dragenter="onDragEnter" @dragleave="onDragLeave" @drop="onDrop" ref="itemsBody" style="position:relative">
				<agenda-row v-for="(rm, idx) in rows" :key="rm.m.key" :item-key="rm.m.key" :object="rm.o" :index="idx" :primary-key="primaryKeyOf(rm.o)">
					<tr v-for="r in structure[rm.m.form ? 'editor' : 'row']" :class="{ 'oddrow':(idx%2 === 1), 'evenrow':(idx%2 === 0), nohover:rm.m.form, errrow:rm.e.length>0 }" :data-idx="idx" v-bind="rowBind(rm)" @dragstart="onDragStart" @dragend="onDragEnd">
						<component v-for="c in r.cells" :is="c.component" :object="rm.o" v-bind="cellBind(c)"></component>
					</tr>
				</agenda-row>
				</tbody>
			</table>
			<div v-if="dragInside" class="drop-marker" :style="positionStyle"></div>
		</template>
		<template v-if="needTail" #tail>
			<template v-for="btn in addButtons">
				<template v-if="visibleButton(btn)">
					<icon-button v-if="btn.label==null" :name="btn.icon" @click.prevent.stop="addItem(btn)" :class="btn.clazz"></icon-button>
					<button v-else v-preview="btn.webPreview" type="button" @click.prevent.stop="addItem(btn)"
							class="fd-array-add-button fbutton input-height" :class="btn.clazz">
						<sym-icon v-if="btn.icon" :name="btn.icon"></sym-icon>
						<span>{{ addLabel(btn) }}</span></button>
				</template>
			</template>
		</template>
	</scroll-frame>
</template>

<script setup lang="ts">
import {ref, computed, provide, onMounted, onUnmounted, reactive, CSSProperties, ComputedRef, inject} from "vue";
import ScrollFrame from "@fd/containers/agenda/ScrollFrame.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import useContainer, {type ContainerProps, containerProps} from "@fd/containers/Container.ts";
import {injectEnv} from "@fd/Environment.ts";
import {AGENDA_START_PADDING, RowContext, useDataGrid} from "@fd/containers/agenda/AgendaUtils.ts";
import createSelected, {rowSelectionKey} from "@fd/containers/agenda/RowSelection.ts";
import {ItemsGrid} from "@t/fd/layout/Agenda.ts";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";
import IconButton from "@fd/helpers/IconButton.vue";
import {ArrayAddButton} from "@t/fd/layout/Array.ts";
import {useAddButton} from "@fd/containers/AddButton.ts";
import AgendaRow from "@fd/containers/agenda/AgendaRow.vue";
import {calcVerticalPosition, objectRef, ObjectRef, setDataTransfer, uidToRef} from "@/utils/DragDropUtils.ts";
import {drawTextLine, TextRun} from "@/bpm/canvas/CanvasUtils.ts";
import {ICON_FONT} from "@/event/calendar/Constants.ts";
import {decodeNumericIconEntity} from "@/event/calendar/Shapes.ts";
import {Icon} from "anneca-symbols";
import set from "lodash.set";
import ApiError from "@fd/helpers/ApiError.vue";
import {ApiError as ApiErrorType} from "@t/util/ApiError.ts";

const uprops = defineProps({
	...containerProps
});
const props = uprops as ContainerProps;
const env = injectEnv();

const itemsContainer = ref(null);
const containerWidth = ref<number>(0);	// net width of the container
const items = computed<ItemsGrid>(() => props.part as ItemsGrid);
const selected = createSelected();

const {visible, visibilityEval, clazz, style, componentOfPart, partRef, localData, ro, vm, prop, formData, errors, findErrors, errorClass, path} = useContainer(props);

function action(name:string, srcType:number, params:Record<string, any>, rc: RowContext, event?:MouseEvent): void {
	switch (name) {
		case "edit":
			toggleEditForKey(rc.itemKey);
			break;
		case "delete":
			deleteItemByKey(rc.itemKey);
			break;
		case "sort-start":	// invoke start of manual sorting
			setDraggableForKey(rc.itemKey, true);
			break;
		case "sort-stop":	// clean after manual sorting
			setDraggableForKey(rc.itemKey, false);
			break;
	}
}

const {structure, cellBind, array, primaryKeyOf} = useDataGrid(items, ro, localData, formData, action);

const tableStyle = computed<CSSProperties>(() => {
	if (structure.value.tableStyle) {
		return { ...style.value, ...structure.value.tableStyle };
	}
	return style.value;
});

let autoIdentityCounter:number = 1;

const meta = reactive<RowMetaData[]>(initialItemsMeta());

function rowBind(rm:RowMeta):Record<string, any> {
	const b:Record<string, any> = { };
	if (rm.m.draggable) {
		b.draggable = true;
	}
	return b;
}

function initialItemsMeta():RowMetaData[] {
	const m:RowMetaData[] = [];
	for (let index = 0; index < array.value.length; index++) {
		m.push(generateMetaForIndex(index));
	}
	return m;
}

function generateMetaForIndex(idx: number, form = false): RowMetaData {
	return {
		key: autoIdentityCounter++,
		form,
		index: idx,
		draggable: false
	};
}



interface RowMetaData {
	/** Unique key of the record */
	key: number;
	/** True if the record is in the edit/view mode */
	form: boolean;
	/** Zero-based index of the record. */
	index: number;
	/** Is true if the row can be dragged by manual sorting */
	draggable: boolean;

	/** To support tree structure */
	children?: RowMetaData[];
}

/** Combines data with corresponding metadata record */
interface RowMeta {
	m: RowMetaData;
	o: any;
	/** Errors found in the row */
	e: ApiErrorType[];
}

const rows = computed<RowMeta[]>(() => {
	const objs = array.value;
	const rm:RowMeta[] = [];
	for (let i = 0; i < objs.length; i++) {
		rm.push({ o: objs[i], m: meta[i], e: findErrors(path+"."+i+".*") } );
	}
	return rm;
});

const addButtons = computed<ArrayAddButton[]>(() => Array.isArray(items.value.add) ? items.value.add : []);
const canAddMore = computed<boolean>(() => addButtons.value.length > 0);
const needTail = computed<boolean>(() => canAddMore.value);


const { addLabel, buildNewItem } = useAddButton(formData, array);

const frameClasses = computed<string[]>(() => {
	const c:string[] = ["items"];
	if (props.insideWidgets) {
		c.push("items-widget");
	}
	return c;
});

function visibleButton(btn: ArrayAddButton): boolean {
	return visibilityEval(btn, "visible");
}

function addItem(btn: ArrayAddButton | null, index?: number) {
	buildNewItem(btn, index).then((newItem) => {
		if (newItem != null) insertToData(newItem, index);
	});
}

function correctMetaIndexes(from = 0):void {
	for (let i = from; i < meta.length; i++) {
		meta[i].index = i;
	}
}

function insertToData(item: any, index?: number) {
	if (typeof index === "number") {
		array.value.splice(index, 0, item);
		meta.splice(index, 0, generateMetaForIndex(index, true));
		correctMetaIndexes(index+1);
		// focusItem(index);
	} else {
		array.value.push(item);
		meta.push(generateMetaForIndex(meta.length, true))
		// focusItem(value.value.length - 1);
	}
}

/** Prepared to work with tree hierarchy */
function keyToIndex(key:string|number):number {
	return findKey(meta, key);
}

function doForKey(key:string|number, found:(idx:number, key:string|number) => void, notFound?:(key:string|number) => void):void {
	const idx = findKey(meta, key);
	if (idx >= 0) found(idx, key);
	else if (notFound) notFound(key);
}

function findKey(collection:RowMetaData[], key:string|number):number {
	return collection.findIndex((item) => item.key === key);
}

function toggleEditForKey(key:string|number):void {
	doForKey(key, (idx) => {
		meta[idx].form = !meta[idx].form;
	});
}

function setDraggableForKey(key:string|number, dg = true):void {
	doForKey(key, (idx) => {
		meta[idx].draggable = dg;
	});
}

function deleteItemByKey(key:string|number):void {
	doForKey(key, (idx) => {
		array.value.splice(idx, 1);
		correctMetaIndexes(idx);
	});
}

function measureContainer(container:HTMLDivElement) {
	if (container != null) {
		const m = getElementMeasure(container);
		containerWidth.value = Math.floor(m.width - (2 * AGENDA_START_PADDING));
	}
}

let resizeObserver = null;

function resizeCallback(entries:ResizeObserverEntry[]) {
	if (entries.length > 0) {
		measureContainer(entries[0].target as HTMLDivElement);
	}
}

function startObserving(el:HTMLDivElement) {
	if (resizeObserver == null) {
		resizeObserver = new ResizeObserver(resizeCallback);
		resizeObserver.observe(el);
		measureContainer(el);
	}
}

function stopObserving() {
	if (resizeObserver != null) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
}

/** DRAG & DROP SUPPORT **/

/** Index of an array item which is just being dragged around */
let draggedIndex = -1;
let dropIndex = -1;
const itemsTable = ref<HTMLTableElement | null>(null);
const itemsBody = ref<HTMLTableSectionElement | null>(null);
const dragInside = ref<boolean>(false);
const cursorY = ref<number>(0);
const cursorWidth = ref<number>(0);
const DRAG_ROW_HEIGHT = 28;
const CELL_GAP = 12;

/** A row starts to being dragged */
function onDragStart(e: DragEvent) {
	draggedIndex = dropIndex = parseInt((e.target as HTMLTableRowElement).dataset.idx);
	if (itemsBody.value) {
		const cells = itemsBody.value.querySelectorAll(":scope > tr[data-idx=\""+draggedIndex+"\"] > td");
		const items:string[] = [];
		cells.forEach(td => {
			const tx = td.textContent.trim();
			if (tx.length > 0) items.push(tx);
		});
		const img = createRowDragImage(items, itemsBody.value); // Function to create your custom drag image
		e.dataTransfer.setDragImage(img.canvas, img.x, img.y);
	}

	e.dataTransfer.effectAllowed = 'move';
	const oref:ObjectRef = objectRef(1111, draggedIndex);
	setDataTransfer(e.dataTransfer, {}, oref);
}


/** Row dragging ended */
function onDragEnd(e: DragEvent) {
	// Cleanup all draggable flags for all rows
	for (let m of meta) {
		m.draggable = false;
	}
	draggedIndex = -1;
	dragInside.value = false;
}

function isIconString(str:string):boolean {
	if (str.length === 1) {
		const code = str.charCodeAt(0);
		return code >= 0xE000 && code <= 0xEFFF;
	}
	return false;
}

const SYS_ICONS: string[] = ["trash", "menu-18"];
for (let i = 0; i < SYS_ICONS.length; i++) {
	SYS_ICONS[i] = decodeNumericIconEntity(Icon.parse(SYS_ICONS[i]).entity);
}

function sysIcon(icon:string):boolean {
	return SYS_ICONS.findIndex((si) => si === icon) >= 0;
}

function createRowDragImage(items:string[], tbody:HTMLTableSectionElement):{ canvas: HTMLCanvasElement, x:number, y: number } {
	const bm = getElementMeasure(tbody);
	const width = Math.min(500, bm.width);
	const height = DRAG_ROW_HEIGHT;
	const canvas:HTMLCanvasElement = document.createElement('canvas');
	canvas.style.width = width + "px";
	canvas.style.height = height + "px";
	canvas.style.position = 'absolute';
	canvas.style.top = "-9000px";
	const ctx = canvas.getContext('2d');
	// Set canvas size
	const ratio = window.devicePixelRatio;
	canvas.width = ratio *  width;
	canvas.height = ratio *  height;
	ctx.scale(ratio, ratio);

	const style = getComputedStyle(tbody);
	const bg = style.getPropertyValue("--bg");
	let fg = style.getPropertyValue("--fg");
	let border = style.getPropertyValue("--border-color");
	let fontSize = style.getPropertyValue("--font-size");
	const ff = style.getPropertyValue("--font");

	try {
		ctx.save();
		const shape = new Path2D();
		ctx.fillStyle = bg;
		shape.roundRect(0.5, 0.5, width - 1, height - 1, 5);
		ctx.fill(shape);
		ctx.strokeStyle = border;
		ctx.stroke(shape);
		// Draw text
		let offset = 6;
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			let tr = null;
			if (isIconString(item)) {
				if (!sysIcon(item)) tr = { text: item, font: "14px "+ICON_FONT, color: fg };
			} else tr = { text: item, font: fontSize+" "+ff, color: fg };
			if (tr != null) {
				offset += drawTextLine(ctx, [ tr ], offset, Math.floor(height/2 + 5), width - offset) + CELL_GAP;
			}
		}
	} finally {
		ctx.restore();
	}

	document.body.appendChild(canvas);
	setTimeout(() => canvas.remove(), 0);
	return { canvas, x: Math.floor(width/2), y: Math.floor(height/2) };
}

function onDragOver(e:DragEvent) {
	if (draggedIndex >= 0 && itemsTable.value != null && itemsBody.value != null) {
		dragInside.value = true;
		e.preventDefault();	// allow dropping
		e.stopPropagation();
		const pos = calcVerticalPosition(e, itemsBody.value, ':scope > tr');
		// Measure offset of TBODY from TABLE
		const tm = getElementMeasure(itemsTable.value);
		const bm = getElementMeasure(itemsBody.value);
		const bodyOffset = bm.top - tm.top;
		//console.log("bodyOffset="+bodyOffset+"   pos=" + JSON.stringify(pos));
		cursorY.value = Math.floor(pos.offset + bodyOffset);
		cursorWidth.value = Math.floor(bm.width);
		dropIndex = pos.index;
	}
}

const positionStyle = computed<CSSProperties>(() => {
	const s:CSSProperties = { width: (cursorWidth.value + "px"), top: (cursorY.value+"px") };
	return s;
});

function onDragEnter(e:DragEvent) {
	onDragOver(e);
}

function onDragLeave(e:DragEvent) {
	//console.log("Drag leave");
	dragInside.value = false;
}
function onDrop(e:DragEvent) {
	dragInside.value = false;
	e.preventDefault();
	const updateNeeded = !(dropIndex === draggedIndex || dropIndex === (draggedIndex + 1));
	if (updateNeeded) {
		// draggedIndex is original index of the item
		// dropIndex is an index of the space where to put the item
		// Remove the item
		const origData = array.value.splice(draggedIndex, 1)[0];
		const origMeta = meta.splice(draggedIndex, 1)[0];
		// Insert the item
		const insertIndex = dropIndex < draggedIndex ? dropIndex : dropIndex - 1;
		array.value.splice(insertIndex, 0, origData);
		meta.splice(insertIndex, 0, origMeta);
		// Update meta indexes
		const seqProp = items.value?.orderProperty ?? null;
		for (let i = 0; i < meta.length; i++) {
			meta[i].index = i;
			if (seqProp) set(array.value[i], seqProp, i);
		}
	}
}

onMounted(() => {
	startObserving(itemsContainer.value.frame);
});
onUnmounted(() => {
	stopObserving();
});

</script>

<style>
.items {
}

.items-widget {
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: baseline;
	/* row-gap: calc(2 * var(--form-v-padding)); */
	row-gap: 0;	/* No separation between head-body-tail */
	column-gap: calc(2 * var(--form-h-padding));
	position: relative;
	--next-left-gutter: var(--form-left-gutter);
	--next-right-gutter: var(--form-right-gutter);
	transition: border-color 500ms;

	margin-left: calc(-1 * var(--form-left-gutter));
	margin-right: calc(-1 * var(--form-right-gutter));
	border-top: 1px solid var(--border-color);
	border-bottom: 1px solid var(--border-color);

	& > .scroll-frame-head,
	& > .scroll-frame-body,
	& > .scroll-frame-tail {
		grid-column: var(--widget-start) / var(--widget-end);
	}
}
</style>